import React, { memo, useMemo } from 'react';
import { shallowEqual, useSelector, useIntl } from 'umi';
import { useRequestController } from '@/pages/aigc/pages/Agent/Home/hooks/useRequestController.ts';
import { CopilotQuestionList } from './CopilotQuestionList';
import { useAgentModelsData } from '../../AgentCollection/hooks';
import { HIDDEN_ORGANIZATIONS } from '@/pages/aigc/components/OrganizationPermission';
import { CopilotGreeting } from './CopilotGreeting';
import Salute from '../img/agent_salute.png';

type TRecommendation = {
  handleClickRecommend: (question: any) => void;
  /** 自定义问候语，传入后将覆盖默认的 Brainy 问候 */
  greetingNode?: React.ReactNode;
};

export const CopilotHomeRecommendation = memo(
  ({ handleClickRecommend, greetingNode }: TRecommendation) => {
    const { locale } = useIntl();
    const { categoryList, selectedAgentId, allAgents } = useAgentModelsData();
    const currentAgent = useMemo(
      () => allAgents?.find((a) => a?.agentId === selectedAgentId),
      [allAgents, selectedAgentId],
    );

    // 根据当前 agent 构建动态问候语（仅当外部未传入 greetingNode 时生效）
    const defaultGreetingNode = useMemo(() => {
      if (!currentAgent) return undefined;
      return locale === 'en-US' ? (
        <>
          I am <span className="dbh-agent-blue-text">{currentAgent.name}</span>
          , {currentAgent.description}
          <img src={Salute} alt="salute-icon" />
        </>
      ) : (
        <>
          我是 <span className="dbh-agent-blue-text">{currentAgent.name_cn}</span>
          ，{currentAgent.description_cn}
          <img src={Salute} alt="salute-icon" />
        </>
      );
    }, [currentAgent, locale]);

    const userInfo = useSelector(
      (state: any) => state.home.userInfo,
      shallowEqual,
    );
    // 获取用户的组织列表
    const userOrganizations: string[] = userInfo?.organization || [];
    // 判断用户是否属于需要替换文本的组织
    const shouldReplaceText = useMemo(() => userOrganizations.some((org: string) =>
      HIDDEN_ORGANIZATIONS.includes(org),
    ), [userOrganizations]);

    const { data, loading } = useRequestController({
      method: 'POST',
      url: `/api/v1/permission/rainbow`,
      refreshDeps: useMemo(() => ({ key: '', group: 'agent.recommend' }), []),
      // 默认10分钟不发起请求
      staleTime: 600000,
      cacheKey: 'copilot-recommendation',
      // 如果是组织，不需要发起这个请求
      manual: shouldReplaceText,
    });

    const realData = useMemo(() => {
      if (shouldReplaceText) {
        return {
          list: categoryList?.[0]?.data?.map(task => ({
            name: task?.questionEn,
            name_cn: task?.questionCn,
          }))
        };
      }
      const dataSource = data?.rows?.slice(0, 1)?.map((row: { value: string; key: string; }) => {
        const parsedValue = JSON.parse(row.value);
        return {
          id: row.key,
          ...parsedValue,
        };
      });
      return { list: dataSource?.[0]?.data };
    }, [data, shouldReplaceText, categoryList]);
    return (
      <>
        <CopilotGreeting tab="aiAgent" greetingNode={greetingNode || defaultGreetingNode}  imgSrc={currentAgent?.icon}/>
        <CopilotQuestionList
          {...{
            data: realData?.list,
            loading,
            handleClickRecommend,
          }}
        />
      </>
    );
  },
);

CopilotHomeRecommendation.displayName = 'CopilotHomeRecommendation';